'use client';

import {
  Content,
  Item,
  type AccordionSingleProps as RadixAccordionProps,
  Root,
  Trigger,
} from '@radix-ui/react-accordion';
import clsx from 'clsx';
import { type ReactNode, useRef, useState } from 'react';

import { BaseContainer, type BaseContainerProps } from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';

import Icon, { type IconType } from '../Icon';
import styles from './index.module.scss';

/**
 * Type definition for an item in the accordion menu.
 */

export type AccordionItem = {
  title: ReactNode;
  children: ReactNode;
};

/**
 * Type definition for Accordion component props.
 *
 * Extends the properties of the Radix Accordion component.
 */

export type AccordionProps = Omit<RadixAccordionProps, 'type'> & {
  items: AccordionItem[];
  onTriggerClick?: (index: number) => void;
  paddingTop?: BaseContainerProps['paddingTop'];
  paddingBottom?: BaseContainerProps['paddingBottom'];
};

/**
 * Accordion component (based on Radix Accordion).
 *
 * @see https://www.radix-ui.com/primitives/docs/components/accordion
 */

export const Accordion = ({
  items,
  className,
  collapsible = true,
  defaultValue,
  onTriggerClick,
  onValueChange,
  paddingTop = 'sm',
  paddingBottom = 'sm',
  ...props
}: AccordionProps) => {
  const [open, setOpen] = useState(defaultValue);
  const contentRefs = useRef<Record<string, HTMLElement | null>>({});

  const handleValueChange = (value: string) => {
    if (!collapsible && !value) return; // Ensure at least one item is open if `collapsible` is false

    Object.keys(contentRefs.current).forEach((id) => {
      const content = contentRefs.current[id];
      if (content) {
        const isOpen = id === value;
        if (isOpen) {
          // Expand the currently opened item
          content.style.height = '0px'; // Reset height
          requestAnimationFrame(() => {
            content.style.height = `${content.scrollHeight}px`; // Expand smoothly
          });
        } else {
          // Collapse all other items
          content.style.height = `${content.scrollHeight}px`; // Set explicit height
          requestAnimationFrame(() => {
            content.style.height = '0px'; // Collapse smoothly
          });
        }
      }
    });

    setOpen(value); // Update the open state
    if (onValueChange) onValueChange(value);
  };

  const renderedItems = items.map((item, index) => {
    const itemId = `item-${index}`;
    const isOpen = open === itemId;
    const triggerIcon: IconType = isOpen ? 'chevronTop' : 'chevronBottom';

    const handleClick = () => {
      if (onTriggerClick) onTriggerClick(index);

      if (isOpen && !collapsible) {
        // Prevent closing the only open item if `collapsible` is false
        return;
      }

      // Toggle open/close for the clicked item
      setOpen((prev) => (prev === itemId ? (collapsible ? undefined : prev) : itemId));
    };

    const classes = clsx(styles.item, isOpen && styles.opened);

    return (
      <Item value={itemId} key={itemId} className={classes}>
        <Trigger asChild>
          <Flex
            role="button"
            align="center"
            border="bottom"
            justify="space-between"
            paddingTop={paddingBottom}
            paddingBottom={paddingBottom}
            className={styles.trigger}
            onClick={handleClick}
          >
            {item.title}
            <Icon type={triggerIcon} className={styles.icon} />
          </Flex>
        </Trigger>
        <Content
          asChild
          forceMount
          ref={(element) => {
            if (element) {
              contentRefs.current[itemId] = element;

              // Reset height after animations complete
              element.addEventListener('transitionend', () => {
                if (element.style.height !== '0px') {
                  element.style.height = 'auto';
                }
              });
            }
          }}
        >
          <BaseContainer className={styles.content}>
            <BaseContainer paddingTop={paddingTop} paddingBottom={paddingBottom}>
              {item.children}
            </BaseContainer>
          </BaseContainer>
        </Content>
      </Item>
    );
  });

  const classes = clsx(styles.accordion, className);

  return (
    <Root
      type="single"
      collapsible={collapsible}
      defaultValue={defaultValue}
      onValueChange={handleValueChange}
      className={classes}
      {...props}
    >
      {renderedItems}
    </Root>
  );
};

export default Accordion;
